import * as React from 'react'
import { Loader2 } from 'lucide-react'
import type { HcpWritesSwitchDto } from '@lib/dto/housecall-pro.dto'
import {
  useHousecallProStatus,
  useSetHcpWrites,
} from '@/hooks/useHousecallPro'
import { HcpStatusMessage } from '@/components/HcpStatusMessage'
import { StatusPill } from '@/components/StatusPill'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { errorMessage } from '@/helpers/logger'

/** "Switched on by Jonathan, Oct 9, 2026, 3:00 PM", or that it never was. */
function changedWords(writes: HcpWritesSwitchDto): string {
  if (writes.changedAt === null) return 'Never switched on.'
  const when = new Date(writes.changedAt).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
  const who = writes.changedByName ?? 'a former user'
  return `Switched ${writes.switchedOn ? 'on' : 'off'} by ${who}, ${when}.`
}

/**
 * Whether this app may create things in the live Housecall Pro account:
 * customers and addresses from a client's page, jobs from an accepted
 * proposal's. Business-wide, so it sits with the business, not under Trees or
 * Irrigation.
 *
 * Switching on asks first; switching off does not, because it only stops
 * things. Only production acts on it, so a development deployment says so
 * rather than looking switched on and doing nothing. Nothing is shown until
 * HCP is connected.
 */
export function HcpWritesSetting() {
  const status = useHousecallProStatus()
  const setWrites = useSetHcpWrites()
  const [confirming, setConfirming] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  if (status.isPending || status.isError) {
    return <HcpStatusMessage status={status} />
  }
  if (!status.data.configured) return null
  const { writes } = status.data

  async function handleSet(enabled: boolean): Promise<void> {
    setError(null)
    try {
      await setWrites.mutateAsync({ enabled })
      setConfirming(false)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h2 className="text-lg font-semibold">Creating in Housecall Pro</h2>
        <p className="text-sm text-secondary-text">
          When this is on, this app can create customers and addresses in
          Housecall Pro from a client’s page, and jobs from an accepted
          proposal’s. Each one is confirmed before it is sent. When it is off,
          this app only reads from Housecall Pro.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <StatusPill tone={writes.switchedOn ? 'green' : 'gray'}>
          {writes.switchedOn ? 'On' : 'Off'}
        </StatusPill>
        <span className="text-sm text-muted-text">{changedWords(writes)}</span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          disabled={setWrites.isPending}
          onClick={() => {
            if (writes.switchedOn) {
              void handleSet(false)
            } else {
              setError(null)
              setConfirming(true)
            }
          }}
        >
          {setWrites.isPending && !confirming ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : null}
          {writes.switchedOn ? 'Turn off' : 'Turn on'}
        </Button>
      </div>

      {writes.allowedHere ? null : (
        <p className="text-xs text-muted-text">
          This is not the production app, so it never writes to Housecall Pro,
          whatever this says.
        </p>
      )}
      {error === null || confirming ? null : (
        <p className="text-sm text-danger">{error}</p>
      )}

      {confirming ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !setWrites.isPending) setConfirming(false)
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Turn on creating in Housecall Pro?</DialogTitle>
              <DialogDescription>
                Anyone signed in to this app will be able to create customers,
                addresses and jobs in the live Housecall Pro account, where
                the office and dispatch will see them. Nothing created there
                can be removed from here.
              </DialogDescription>
            </DialogHeader>
            {error === null ? null : (
              <p className="text-sm text-danger">{error}</p>
            )}
            <DialogFooter>
              <Button
                size="sm"
                variant="outline"
                disabled={setWrites.isPending}
                onClick={() => {
                  setConfirming(false)
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={setWrites.isPending}
                onClick={() => {
                  void handleSet(true)
                }}
              >
                {setWrites.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : null}
                Turn on
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </section>
  )
}
